import React, { useState } from 'react'
import {
  FileInput,
  Settings2,
  Car,
  Palette,
  Building2,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  HelpCircle,
  ChevronRight,
} from 'lucide-react'
import styles from './PipelineVisualization.module.scss'

export interface AuditStep {
  step: string
  status: string
  timestamp: string
  message?: string
  data_snapshot?: Record<string, unknown>
  [key: string]: unknown
}

interface PipelineVisualizationProps {
  auditHistory: AuditStep[]
  onStepClick?: (step: AuditStep) => void
}

// Map step names to icons and display labels
const STEP_CONFIG: Record<string, { icon: React.ReactNode; label: string; order: number }> = {
  raw_input: { icon: <FileInput size={16} />, label: 'Input', order: 1 },
  file_received: { icon: <FileInput size={16} />, label: 'File Received', order: 1 },
  parsing: { icon: <Settings2 size={16} />, label: 'Parsing', order: 2 },
  parsed: { icon: <Settings2 size={16} />, label: 'Parsed', order: 2 },
  normalization: { icon: <Settings2 size={16} />, label: 'Normalization', order: 3 },
  normalized: { icon: <Settings2 size={16} />, label: 'Normalized', order: 3 },
  vin_decode: { icon: <Car size={16} />, label: 'VIN Decode', order: 4 },
  vin_decoded: { icon: <Car size={16} />, label: 'VIN Decoded', order: 4 },
  make_model: { icon: <Car size={16} />, label: 'Make/Model', order: 5 },
  make_model_transform: { icon: <Car size={16} />, label: 'Make/Model', order: 5 },
  color_transform: { icon: <Palette size={16} />, label: 'Colors', order: 6 },
  color_mapping: { icon: <Palette size={16} />, label: 'Colors', order: 6 },
  dealer_lookup: { icon: <Building2 size={16} />, label: 'Dealer', order: 7 },
  dealer_mapped: { icon: <Building2 size={16} />, label: 'Dealer', order: 7 },
  validation: { icon: <CheckCircle2 size={16} />, label: 'Validation', order: 8 },
  validated: { icon: <CheckCircle2 size={16} />, label: 'Validated', order: 8 },
  api_push: { icon: <Send size={16} />, label: 'API Push', order: 9 },
  published: { icon: <Send size={16} />, label: 'Published', order: 10 },
  pusher_queued: { icon: <Clock size={16} />, label: 'Queued', order: 9 },
  pusher_success: { icon: <CheckCircle2 size={16} />, label: 'Pushed', order: 10 },
  pusher_failed: { icon: <XCircle size={16} />, label: 'Push Failed', order: 10 },
  success: { icon: <CheckCircle2 size={16} />, label: 'Success', order: 11 },
  failed: { icon: <XCircle size={16} />, label: 'Failed', order: 11 },
  error: { icon: <XCircle size={16} />, label: 'Error', order: 11 },
}

const getStepConfig = (stepName: string) => {
  const normalized = stepName.toLowerCase().replace(/[^a-z0-9_]/g, '_')
  return STEP_CONFIG[normalized] || { icon: <HelpCircle size={16} />, label: stepName, order: 99 }
}

const getStatusColor = (status: string): string => {
  const s = status.toLowerCase()
  if (s === 'success' || s === 'completed' || s === 'published') return 'success'
  if (s === 'failed' || s === 'error') return 'failed'
  if (s === 'pending' || s === 'queued' || s === 'processing') return 'pending'
  return 'neutral'
}

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return timestamp
  }
}

const PipelineVisualization: React.FC<PipelineVisualizationProps> = ({ auditHistory, onStepClick }) => {
  const [selectedStep, setSelectedStep] = useState<number | null>(null)

  // Sort steps by order (based on step name) and then by timestamp
  const sortedSteps = [...auditHistory].sort((a, b) => {
    const orderA = getStepConfig(a.step).order
    const orderB = getStepConfig(b.step).order
    if (orderA !== orderB) return orderA - orderB
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  })

  const handleStepClick = (step: AuditStep, index: number) => {
    setSelectedStep(selectedStep === index ? null : index)
    onStepClick?.(step)
  }

  if (!auditHistory || auditHistory.length === 0) {
    return (
      <div className={styles.emptyPipeline}>
        <HelpCircle size={24} />
        <span>No pipeline steps recorded</span>
      </div>
    )
  }

  // Determine overall pipeline status
  const lastStep = sortedSteps[sortedSteps.length - 1]
  const overallStatus = getStatusColor(lastStep?.status || 'pending')

  return (
    <div className={styles.pipelineContainer}>
      <div className={styles.pipelineHeader}>
        <span className={styles.pipelineTitle}>Processing Pipeline</span>
        <span className={`${styles.pipelineStatus} ${styles[overallStatus]}`}>{sortedSteps.length} steps</span>
      </div>

      <div className={styles.pipeline}>
        {sortedSteps.map((step, index) => {
          const config = getStepConfig(step.step)
          const statusColor = getStatusColor(step.status)
          const isLast = index === sortedSteps.length - 1
          const isSelected = selectedStep === index

          return (
            <React.Fragment key={`${step.step}-${index}`}>
              <div
                className={`${styles.step} ${styles[statusColor]} ${isSelected ? styles.selected : ''}`}
                onClick={() => handleStepClick(step, index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleStepClick(step, index)}>
                <div className={styles.stepNode}>
                  <div className={styles.stepIcon}>{config.icon}</div>
                </div>
                <div className={styles.stepLabel}>{config.label}</div>
                <div className={styles.stepTime}>{formatTimestamp(step.timestamp)}</div>
              </div>

              {!isLast && (
                <div className={`${styles.connector} ${styles[statusColor]}`}>
                  <div className={styles.connectorLine} />
                  <ChevronRight size={12} className={styles.connectorArrow} />
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>

      {selectedStep !== null && sortedSteps[selectedStep] && (
        <div className={styles.stepDetails}>
          <div className={styles.detailsHeader}>
            <span className={styles.detailsTitle}>{getStepConfig(sortedSteps[selectedStep].step).label} Details</span>
            <span className={`${styles.detailsStatus} ${styles[getStatusColor(sortedSteps[selectedStep].status)]}`}>
              {sortedSteps[selectedStep].status}
            </span>
          </div>
          <div className={styles.detailsBody}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Step</span>
              <span className={styles.detailValue}>{sortedSteps[selectedStep].step}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Timestamp</span>
              <span className={styles.detailValue}>{formatTimestamp(sortedSteps[selectedStep].timestamp)}</span>
            </div>
            {sortedSteps[selectedStep].message && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Message</span>
                <span className={styles.detailValue}>{sortedSteps[selectedStep].message}</span>
              </div>
            )}
            {sortedSteps[selectedStep].data_snapshot && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Data Snapshot</span>
                <pre className={styles.dataSnapshot}>
                  {JSON.stringify(sortedSteps[selectedStep].data_snapshot, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PipelineVisualization
